import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { GraduationCap, Loader2, ArrowRight, AlertTriangle, CheckCircle2, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

export default function PedagogicalMode({ projectId, juridique }) {
  const [idx, setIdx] = useState(0);
  const [rewrites, setRewrites] = useState({}); // { clauseIdx: {..result} }
  const [busy, setBusy] = useState(false);

  const abus = juridique?.clauses_abusives || [];

  if (abus.length === 0) {
    return (
      <div className="rap-card p-8 text-center" data-testid="pedagogical-empty">
        <GraduationCap size={32} className="mx-auto mb-3 opacity-40" />
        <h2 className="font-display text-lg font-bold mb-1">Mode Lecture Pédagogique</h2>
        <p className="text-sm opacity-70">
          Aucune clause abusive détectée sur ce projet.
        </p>
        <p className="text-xs opacity-60 mt-2">
          Lance d'abord l'analyse juridique pour identifier les clauses à réécrire.
        </p>
      </div>
    );
  }

  const current = abus[idx];
  const rewrite = rewrites[idx];

  const generateRewrite = async () => {
    if (rewrite) return; // already computed
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/amendment/rewrite`, {
        original: current.texte_exact || current.clause_id || "",
        problem: current.analyse || current.type_abus || "",
      });
      setRewrites((r) => ({ ...r, [idx]: data }));
      if (data._warning) toast.warning(data._warning);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur LLM");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="pedagogical-mode">
      <div className="rap-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-display text-lg font-bold flex items-center gap-2">
              <GraduationCap size={18} style={{ color: "#D4A017" }} />
              Mode Lecture Pédagogique
            </h2>
            <p className="text-xs opacity-70 mt-1">
              Pour chaque clause abusive détectée, compare la version originale (rouge) avec
              une réécriture équilibrée (vert) générée par GPT-4o.
            </p>
          </div>
          <div className="font-mono text-sm opacity-70">
            {idx + 1} / {abus.length}
          </div>
        </div>

        {/* Nav */}
        <div className="flex items-center gap-2 flex-wrap">
          {abus.map((c, i) => (
            <button key={i} onClick={() => setIdx(i)}
              className={`px-3 py-1 rounded-sm text-xs font-mono transition-colors ${i === idx ? "font-bold" : "opacity-60 hover:opacity-100"}`}
              style={{
                background: i === idx ? "rgba(212,160,23,0.15)" : "transparent",
                color: i === idx ? "#D4A017" : "inherit",
                border: `1px solid ${i === idx ? "#D4A017" : "hsl(var(--border))"}`,
              }}
              data-testid={`pedago-nav-${i}`}>
              {i + 1}. {c.type_abus?.replace(/_/g, " ").slice(0, 25)}
            </button>
          ))}
        </div>
      </div>

      {/* Split-screen */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Left: abusive */}
        <div className="rap-card p-5"
          style={{ borderColor: "rgba(192,57,43,0.4)", background: "rgba(192,57,43,0.03)" }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} style={{ color: "#C0392B" }} />
            <h3 className="font-display font-bold" style={{ color: "#C0392B" }}>
              AVANT — Clause abusive
            </h3>
            <span className="ml-auto font-mono text-[10px] px-2 py-0.5 rounded-sm font-bold"
              style={{ background: "rgba(192,57,43,0.2)", color: "#C0392B" }}>
              {current.gravite?.toUpperCase()}
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Type d'abus</div>
              <div className="text-sm font-semibold">{current.type_abus}</div>
            </div>
            {current.clause_id && (
              <div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Référence</div>
                <div className="font-mono text-xs">{current.clause_id}</div>
              </div>
            )}
            {current.texte_exact && (
              <div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Texte exact</div>
                <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap border rounded-sm p-3"
                  style={{ borderColor: "rgba(192,57,43,0.3)", background: "rgba(192,57,43,0.05)" }}>
                  {current.texte_exact}
                </div>
              </div>
            )}
            {current.analyse && (
              <div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Pourquoi c'est abusif</div>
                <div className="text-xs leading-relaxed opacity-90">{current.analyse}</div>
              </div>
            )}
            {current.impact_negatif && (
              <div className="border-l-2 pl-3 py-1" style={{ borderColor: "#C0392B" }}>
                <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "#C0392B" }}>Impact</div>
                <div className="text-xs">{current.impact_negatif}</div>
              </div>
            )}
          </div>
        </div>

        {/* Right: rewrite */}
        <div className="rap-card p-5"
          style={{ borderColor: "rgba(39,174,96,0.4)", background: "rgba(39,174,96,0.03)" }}>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={16} style={{ color: "#27AE60" }} />
            <h3 className="font-display font-bold" style={{ color: "#27AE60" }}>
              APRÈS — Clause rééquilibrée
            </h3>
            {rewrite && (
              <button
                className="ml-auto opacity-70 hover:opacity-100"
                onClick={() => {
                  navigator.clipboard.writeText(rewrite.clause_proposee || "");
                  toast.success("Copié");
                }}
                title="Copier la clause réécrite"
                data-testid="pedago-copy"
              >
                <Copy size={12} />
              </button>
            )}
          </div>

          {!rewrite ? (
            <div className="text-center py-8">
              <Sparkles size={24} className="mx-auto mb-3 opacity-40" />
              <p className="text-xs opacity-70 mb-4">
                Clique pour générer la version équilibrée avec GPT-4o
              </p>
              <Button onClick={generateRewrite} disabled={busy}
                className="rounded-sm font-semibold"
                style={{ background: "#27AE60", color: "white" }}
                data-testid="pedago-generate">
                {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ArrowRight size={14} className="mr-2" />}
                Générer la réécriture
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {rewrite._warning && (
                <div className="text-[10px] italic p-2 rounded-sm"
                  style={{ background: "rgba(230,126,34,0.1)", color: "#E67E22" }}>
                  {rewrite._warning}
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Clause proposée</div>
                <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap border rounded-sm p-3"
                  style={{ borderColor: "rgba(39,174,96,0.3)", background: "rgba(39,174,96,0.05)" }}>
                  {rewrite.clause_proposee}
                </div>
              </div>
              {(rewrite.modifications_clés || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Modifications clés</div>
                  <ul className="text-xs space-y-1 list-disc list-inside opacity-90">
                    {rewrite.modifications_clés.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}
              {rewrite.justification_juridique && (
                <div className="text-xs border-l-2 pl-3 py-1"
                  style={{ borderColor: "#27AE60" }}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Fondement</div>
                  {rewrite.justification_juridique}
                </div>
              )}
              {(rewrite.references_normatives || []).length > 0 && (
                <div className="text-xs">
                  <span className="opacity-70">Références : </span>
                  {rewrite.references_normatives.join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Button onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0} size="sm" variant="outline" className="rounded-sm"
          data-testid="pedago-prev">
          ← Clause précédente
        </Button>
        <Button onClick={() => setIdx((i) => Math.min(abus.length - 1, i + 1))}
          disabled={idx === abus.length - 1} size="sm"
          className="rounded-sm font-semibold"
          style={{ background: "#1B4332", color: "white" }}
          data-testid="pedago-next">
          Clause suivante →
        </Button>
      </div>
    </div>
  );
}
